import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import OfflineMemberHome from './OfflineMemberHome';
import { supabase } from './supabase';
import { authRedirectUrl, isNativeShell } from './authRedirect';
import { cacheApprovedMember, clearApprovedMemberCache, readApprovedMemberCache } from './offlineMemberAccess';
import {
  authenticateNativeBiometric,
  clearNativeBiometricSession,
  getNativeBiometricStatus,
  setNativeBiometricEnabled,
} from '../native/nativeSecureStorage';
import NotificationRuntime from '../notifications/NotificationRuntime';
import './member-auth.css';

const REMS_MEMBER_STORAGE_KEY = 'smfd-rems-member-name';

function AuthBrandMark() {
  return <img className="member-auth-mark" src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />;
}

function friendlyError(error) {
  const message = String(error?.message || error || 'Something went wrong.');
  if (/invalid login credentials/i.test(message)) return 'Incorrect organization email or password.';
  if (/email not confirmed/i.test(message)) return 'Check your organization email and finish account activation first.';
  return message;
}

function isDistrictEmail(value) {
  return /^[^\s@]+@metrofire\.ca\.gov$/i.test(String(value || '').trim());
}


function isIndividualRemsProfile(profile) {
  return ['rems_temporary', 'rems_permanent'].includes(String(profile?.member_kind || ''));
}

function previewBrowserTestMode() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  return host.endsWith('.vercel.app') && host !== 'sacmetrolinemedic.app';
}

function isPasswordSetupPath() {
  return typeof window !== 'undefined'
    && (window.location.pathname.replace(/\/+$/, '') || '/') === '/account/update-password';
}

function handoffReturnPath() {
  if (typeof window === 'undefined') return '';
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  if (!/^\/handoff\/[A-Za-z0-9_-]+$/i.test(pathname)) return '';
  return `${pathname}${window.location.search || ''}`;
}

function remsEnrollmentUrl() {
  const returnTo = handoffReturnPath();
  return returnTo ? `/rems-access?returnTo=${encodeURIComponent(returnTo)}` : '/rems-access';
}


function connectivityFailure(error) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return /failed to fetch|network|load failed|timeout|timed out|offline|connection/i.test(String(error?.message || error || ''));
}

export default function MemberAuthGate({ children }) {
  const nativeShell = isNativeShell();
  const previewBrowserTest = previewBrowserTestMode();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('checking');
  const [mode, setMode] = useState(isPasswordSetupPath() ? 'update-password' : 'signin');
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  const [biometric, setBiometric] = useState(() => ({
    ready: !nativeShell,
    success: true,
    available: false,
    enabled: false,
    configured: !nativeShell,
    biometryType: 'none',
    label: 'Biometrics',
    message: '',
  }));
  const [biometricUnlocked, setBiometricUnlocked] = useState(!nativeShell);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [biometricError, setBiometricError] = useState('');
  const biometricPrompted = useRef(false);

  const loadProfile = useCallback(async (activeSession, options = {}) => {
    const silent = options?.silent === true;
    if (!activeSession) {
      setProfile(null);
      setStatus('signed-out');
      return;
    }

    if (isPasswordSetupPath()) {
      setMode('update-password');
      setStatus('password-recovery');
      return;
    }

    if (!silent) setStatus('checking-access');
    const { data, error: approvalError } = await supabase.rpc('refresh_member_approval');
    if (approvalError) {
      const cached = connectivityFailure(approvalError) ? readApprovedMemberCache(activeSession) : null;
      if (cached) {
        setProfile(cached);
        setError('');
        setStatus('approved');
        return;
      }
      setError(friendlyError(approvalError));
      setStatus('error');
      return;
    }

    const nextProfile = data || null;
    setProfile(nextProfile);
    if (nextProfile?.disabled) {
      clearApprovedMemberCache(activeSession);
      setStatus('disabled');
      return;
    }

    if (nextProfile?.approved && !nextProfile?.activated_at) {
      clearApprovedMemberCache(activeSession);
      setStatus('activation-required');
      return;
    }

    if (nextProfile?.approved) {
      cacheApprovedMember(nextProfile, activeSession);
      setStatus('approved');
      return;
    }
    setStatus('pending');
  }, []);

  useEffect(() => {
    if (!nativeShell) return undefined;
    let mounted = true;
    getNativeBiometricStatus().then(next => {
      if (!mounted) return;
      setBiometric({ ...next, ready: true });
      if (!next.enabled) setBiometricUnlocked(true);
    });
    return () => { mounted = false; };
  }, [nativeShell]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const current = data?.session || null;
      setSession(current);
      if (isPasswordSetupPath() && current) {
        setMode('update-password');
        setStatus('password-recovery');
      } else {
        loadProfile(current);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession || null);
      setError('');
      if (event === 'PASSWORD_RECOVERY') {
        setMode('update-password');
        setStatus('password-recovery');
        return;
      }
      // Supabase may refresh its token when the native IAP browser closes.
      // Revalidate approval silently so an in-progress Saved IAP flow is not
      // unmounted and reset back to the dashboard.
      loadProfile(nextSession || null, { silent: event !== 'SIGNED_OUT' });
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [loadProfile]);

  useEffect(() => {
    const wentOnline = () => {
      setOnline(true);
      if (session) loadProfile(session, { silent: true });
    };
    const wentOffline = () => setOnline(false);
    window.addEventListener('online', wentOnline);
    window.addEventListener('offline', wentOffline);
    return () => {
      window.removeEventListener('online', wentOnline);
      window.removeEventListener('offline', wentOffline);
    };
  }, [session, loadProfile]);

  const unlockWithBiometric = useCallback(async () => {
    if (!nativeShell || biometricBusy) return;
    setBiometricBusy(true);
    setBiometricError('');
    const result = await authenticateNativeBiometric('Unlock Line Medic');
    setBiometric(value => ({ ...value, ...result, ready: true }));
    if (result.success) setBiometricUnlocked(true);
    else setBiometricError(result.message || `${result.label || 'Biometric'} authentication was not completed.`);
    setBiometricBusy(false);
  }, [nativeShell, biometricBusy]);

  useEffect(() => {
    if (
      status !== 'approved'
      || !profile
      || !nativeShell
      || !biometric.ready
      || !biometric.enabled
      || biometricUnlocked
      || biometricBusy
      || biometricPrompted.current
    ) return;

    biometricPrompted.current = true;
    unlockWithBiometric();
  }, [status, profile, nativeShell, biometric, biometricUnlocked, biometricBusy, unlockWithBiometric]);

  const signIn = async event => {
    event.preventDefault();
    setError('');
    setMessage('');
    setStatus('working');
    clearNativeBiometricSession();
    biometricPrompted.current = false;
    setBiometricUnlocked(!nativeShell || !biometric.enabled);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: form.email.trim().toLowerCase(), password: form.password });
    if (signInError) {
      setError(friendlyError(signInError));
      setStatus('signed-out');
    }
  };

  const activateAccount = async event => {
    event.preventDefault();
    setError('');
    setMessage('');
    const districtEmail = form.email.trim().toLowerCase();
    if (!isDistrictEmail(districtEmail)) return setError('Use the organization email configured for this deployment.');

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(districtEmail, {
      redirectTo: authRedirectUrl('/account/update-password'),
    });
    if (resetError) return setError(friendlyError(resetError));

    setMessage('If this email is on the approved Line Medic roster, an activation email is on the way. Open it and choose your password.');
  };

  const requestReset = async event => {
    event.preventDefault();
    setError('');
    setMessage('');
    const districtEmail = form.email.trim().toLowerCase();
    if (!isDistrictEmail(districtEmail)) return setError('Use the organization email configured for this deployment.');
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(districtEmail, {
      redirectTo: authRedirectUrl('/account/update-password'),
    });
    if (resetError) return setError(friendlyError(resetError));
    setMessage('Password reset email sent.');
  };

  const updatePassword = async event => {
    event.preventDefault();
    setError('');
    if (form.password.length < 8) return setError('Use at least 8 characters for your password.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
    const { error: updateError } = await supabase.auth.updateUser({ password: form.password });
    if (updateError) return setError(friendlyError(updateError));

    window.history.replaceState({}, '', '/');
    setMode('signin');
    setMessage('Password set. Your Line Medic account is active.');
    const { data: sessionData } = await supabase.auth.getSession();
    const activeSession = sessionData?.session || session;
    setSession(activeSession || null);
    await loadProfile(activeSession || null);
  };

  const signOut = async () => {
    clearApprovedMemberCache(session);
    clearNativeBiometricSession();
    await supabase.auth.signOut();
    if (typeof window !== 'undefined') {
      window.__SMFD_MEMBER_ID__ = '';
      window.localStorage?.removeItem(REMS_MEMBER_STORAGE_KEY);
    }
    biometricPrompted.current = false;
    setBiometricUnlocked(!nativeShell);
    setBiometricError('');
    setProfile(null);
    setSession(null);
    setMode('signin');
    setStatus('signed-out');
  };

  const enableBiometricUnlock = async () => {
    setBiometricBusy(true);
    setBiometricError('');
    const result = await setNativeBiometricEnabled(true);
    setBiometric(value => ({ ...value, ...result, ready: true }));
    if (result.enabled && result.success) {
      setBiometricUnlocked(true);
      biometricPrompted.current = true;
    } else {
      setBiometricError(result.message || 'Biometric unlock was not enabled.');
    }
    setBiometricBusy(false);
  };

  const skipBiometricSetup = async () => {
    setBiometricBusy(true);
    const result = await setNativeBiometricEnabled(false);
    setBiometric(value => ({ ...value, ...result, ready: true, configured: true, enabled: false }));
    setBiometricUnlocked(true);
    setBiometricError('');
    setBiometricBusy(false);
  };

  const usePasswordInstead = async () => {
    setBiometricBusy(true);
    const result = await setNativeBiometricEnabled(false);
    setBiometric(value => ({ ...value, ...result, ready: true, configured: true, enabled: false }));
    setBiometricBusy(false);
    await signOut();
  };

  const switchMode = nextMode => {
    setMode(nextMode);
    setError('');
    setMessage('');
    setForm(value => ({ ...value, password: '', confirmPassword: '' }));
  };

  if (status === 'approved' && profile) {
    if (typeof window !== 'undefined') {
      window.__SMFD_MEMBER_ID__ = String(profile.id || profile.email || profile.full_name || '').trim();
    }

    const individualRems = isIndividualRemsProfile(profile);
    const remsPasswordReady = session?.user?.user_metadata?.rems_password_set === true;

    if (individualRems && !remsPasswordReady) {
      return (
        <div className="member-auth-root">
          <section className="member-auth-card">
            <AuthBrandMark />
            <h1>Finish REMS Account Setup</h1>
            <p><strong>{profile.full_name}</strong></p>
            <div className="member-auth-note">Your mobile number was verified, but this REMS account still needs a password and biometric setup before it can be used.</div>
            <button type="button" className="member-auth-primary" onClick={() => window.location.assign(remsEnrollmentUrl())}>Continue REMS Setup</button>
            <button type="button" className="member-auth-link" onClick={signOut}>Sign out</button>
          </section>
        </div>
      );
    }

    if (nativeShell && !biometric.ready) {
      return <div className="member-auth-root"><section className="member-auth-card"><AuthBrandMark /><h1>Line Medic</h1><p>Preparing secure unlock…</p></section></div>;
    }

    if (nativeShell && individualRems && biometric.ready && !biometric.available) {
      return (
        <div className="member-auth-root">
          <section className="member-auth-card">
            <AuthBrandMark />
            <h1>Face ID Required</h1>
            <p>Individual REMS access requires Face ID or Touch ID on this iPhone.</p>
            <div className="member-auth-note">Enable device biometrics in iPhone Settings, then reopen Line Medic.</div>
            <button type="button" className="member-auth-link" onClick={signOut}>Sign out</button>
          </section>
        </div>
      );
    }

    if (nativeShell && biometric.available && (!biometric.configured || (individualRems && !biometric.enabled))) {
      return (
        <div className="member-auth-root">
          <section className="member-auth-card">
            <AuthBrandMark />
            <h1>{individualRems ? `${biometric.label} Required` : `Use ${biometric.label}?`}</h1>
            <p>{individualRems ? `Secure this iPhone with ${biometric.label} to continue with REMS access.` : `Unlock Line Medic with ${biometric.label} on this iPhone instead of entering your password each time.`}</p>
            <div className="member-auth-note">{individualRems ? `Individual REMS accounts require ${biometric.label}. Gear Room unlocks also require a fresh biometric check.` : <>Your password is never stored for biometric login. {biometric.label} only unlocks the secure session already stored in the iPhone Keychain, and it works without cell service.</>}</div>
            {biometricError && <div className="member-auth-error">{biometricError}</div>}
            <button type="button" className="member-auth-primary" disabled={biometricBusy} onClick={enableBiometricUnlock}>{biometricBusy ? 'Setting up…' : `Enable ${biometric.label}`}</button>
            {!individualRems && <button type="button" className="member-auth-link" disabled={biometricBusy} onClick={skipBiometricSetup}>Not now</button>}
          </section>
        </div>
      );
    }

    if (nativeShell && biometric.enabled && !biometricUnlocked) {
      return (
        <div className="member-auth-root">
          <section className="member-auth-card">
            <AuthBrandMark />
            <h1>Unlock Line Medic</h1>
            <p>Use {biometric.label} to continue.</p>
            {biometricError && <div className="member-auth-error">{biometricError}</div>}
            <button type="button" className="member-auth-primary" disabled={biometricBusy} onClick={unlockWithBiometric}>{biometricBusy ? `Checking ${biometric.label}…` : `Unlock with ${biometric.label}`}</button>
            <button type="button" className="member-auth-link" disabled={biometricBusy} onClick={usePasswordInstead}>Use password instead</button>
          </section>
        </div>
      );
    }

    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const offlineRestricted = profile?._offline_access === true || !online;
    if (offlineRestricted && !['/my-gear', '/tracking', '/ics214'].includes(path)) return <OfflineMemberHome profile={profile} />;
    const approvedContent = typeof children === 'function' ? children(profile, session) : children;
    return (
      <>
        <NotificationRuntime profile={profile} />
        {approvedContent}
      </>
    );
  }

  if (status === 'disabled' && profile) {
    return <div className="member-auth-root"><section className="member-auth-card"><AuthBrandMark /><h1>Access disabled</h1><p><strong>{profile.full_name}</strong></p><p>This Line Medic account has been disabled by the program administrator.</p><div className="member-auth-note">If access should be restored, contact the Line Medic Program administrator.</div><button type="button" className="member-auth-link" onClick={signOut}>Sign out</button></section></div>;
  }

  if (status === 'activation-required' && profile) {
    return <div className="member-auth-root"><section className="member-auth-card"><AuthBrandMark /><h1>Finish account activation</h1><p><strong>{profile.full_name}</strong></p><p>Your roster access is approved, but your account does not have a completed password activation yet.</p><div className="member-auth-note">Complete the password setup before Line Medic member resources can open.</div><button type="button" className="member-auth-primary" onClick={() => window.location.assign('/account/update-password')}>Continue password setup</button><button type="button" className="member-auth-link" onClick={signOut}>Sign out</button></section></div>;
  }

  if (status === 'pending' && profile) {
    return <div className="member-auth-root"><section className="member-auth-card"><AuthBrandMark /><h1>Access pending</h1><p><strong>{profile.full_name}</strong></p><p>Your organization account is signed in, but program access has not been approved yet.</p><div className="member-auth-note">Contact a Line Medic Program administrator if you should already be on the approved roster.</div><button type="button" className="member-auth-primary" onClick={() => loadProfile(session)}>Check access again</button><button type="button" className="member-auth-link" onClick={signOut}>Sign out</button></section></div>;
  }

  if (['checking', 'checking-access', 'working'].includes(status)) {
    return <div className="member-auth-root"><section className="member-auth-card"><AuthBrandMark /><h1>Line Medic</h1><p>Checking your account…</p></section></div>;
  }

  const isActivate = !nativeShell && mode === 'activate';
  const isForgot = mode === 'forgot';
  const isUpdate = mode === 'update-password';

  return (
    <div className="member-auth-root">
      <section className="member-auth-card">
        <AuthBrandMark />
        <h1>{isActivate ? 'Activate Line Medic Account' : isForgot ? 'Reset password' : isUpdate ? 'Choose your password' : 'Line Medic Sign In'}</h1>
        <p className="member-auth-subtitle">
          {isActivate
            ? 'Already on the approved roster? Verify your organization email and choose your own password.'
            : isUpdate
              ? 'Create a password for your Line Medic account.'
              : nativeShell || previewBrowserTest
                ? 'Line Medic members and existing REMS members sign in below. New SMFD REMS members can create a temporary account with REMS Quick Access.'
                : 'Use the authorized organization account provided by your program.'}
        </p>

        {!isUpdate && (
          <div className="member-auth-note">
            {isActivate
              ? <>Enter the organization email configured for this Line Medic deployment. If you are preapproved, we’ll send your secure activation link.</>
              : nativeShell || previewBrowserTest
                ? <>Already have a Line Medic or REMS account? Sign in above with your organization email and password. REMS Quick Access below is only for new REMS accounts.</>
                : <>Use your authorized organization email.</>}
          </div>
        )}
        {error && <div className="member-auth-error">{error}</div>}
        {message && <div className="member-auth-success">{message}</div>}

        {!isActivate && !isForgot && !isUpdate && (
          <>
            <form onSubmit={signIn} className="member-auth-form">
              <label>Organization email
                <input
                  type="text"
                  autoComplete="username"
                  value={form.email}
                  onChange={e => setForm(v => ({ ...v, email: e.target.value }))}
                  placeholder="name@agency.gov"
                  required
                />
              </label>
              <label>Password
                <input type="password" autoComplete="current-password" value={form.password} onChange={e => setForm(v => ({ ...v, password: e.target.value }))} required />
              </label>
              <button className="member-auth-primary" type="submit">Sign in</button>
            </form>

            {(nativeShell || previewBrowserTest) && (
              <div className="member-auth-rems-entry">
                <div className="member-auth-rems-kicker">SMFD REMS</div>
                <strong>New REMS member without an account?</strong>
                <p>{previewBrowserTest ? 'Use REMS Quick Access to create a temporary Preview test account. We’ll verify your mobile number, then you’ll create a password for Gear Handoff testing.' : 'Use REMS Quick Access only if you do not already have a Line Medic or REMS account. We’ll verify your mobile number, then you’ll create a password and enable Face ID.'}</p>
                <button type="button" className="member-auth-rems-button" onClick={() => window.location.assign(remsEnrollmentUrl())}>
                  REMS Quick Access
                </button>
                <small>{previewBrowserTest ? 'Preview only: Gmail test identities are allowed here. Production remains restricted to authorized organization accounts.' : 'Existing REMS members should sign in normally above. New accounts receive 30-day temporary access pending Program Admin verification.'}</small>
              </div>
            )}
          </>
        )}

        {isActivate && (
          <form onSubmit={activateAccount} className="member-auth-form">
            <label>Organization email
              <input type="email" autoComplete="email" value={form.email} onChange={e => setForm(v => ({ ...v, email: e.target.value }))} placeholder="name@agency.gov" required />
            </label>
            <button className="member-auth-primary" type="submit">Send Activation Email</button>
            <div className="member-auth-note">No temporary password is used. The password you choose is private to you and stored securely by Supabase Auth.</div>
          </form>
        )}

        {isForgot && (
          <form onSubmit={requestReset} className="member-auth-form">
            <label>Organization email
              <input type="email" autoComplete="email" value={form.email} onChange={e => setForm(v => ({ ...v, email: e.target.value }))} placeholder="name@agency.gov" required />
            </label>
            <button className="member-auth-primary" type="submit">Send Reset Email</button>
          </form>
        )}

        {isUpdate && (
          <form onSubmit={updatePassword} className="member-auth-form">
            <label>New password<input type="password" autoComplete="new-password" value={form.password} onChange={e => setForm(v => ({ ...v, password: e.target.value }))} required /></label>
            <label>Confirm new password<input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={e => setForm(v => ({ ...v, confirmPassword: e.target.value }))} required /></label>
            <button className="member-auth-primary" type="submit">Set Password & Activate</button>
          </form>
        )}

        {!isUpdate && (
          <div className="member-auth-links">
            {mode !== 'signin' && <button type="button" onClick={() => switchMode('signin')}>Sign in</button>}
            {!nativeShell && mode !== 'activate' && <button type="button" onClick={() => switchMode('activate')}>Activate account</button>}
            {mode !== 'forgot' && <button type="button" onClick={() => switchMode('forgot')}>Forgot password?</button>}
          </div>
        )}

        {nativeShell && !isForgot && !isUpdate && (
          <>
            <div className="member-auth-note">Apple App Review can use the full sanitized demonstration environment below. It never connects to live Line Medic operational data.</div>
            <button type="button" className="member-auth-link" onClick={() => window.location.assign('/app-review-demo')}>Apple App Review Demo</button>
          </>
        )}
      </section>
    </div>
  );
}
